<template>
  <div v-if="membershipPagerLoaded" class="memberships-tab">
    <header class="memberships-header">
      <div>
        <h3 class="page-header-2 mb-0">Memberships</h3>
        <p class="memberships-subtitle">{{ subtitleText }}</p>
      </div>
      <SeraButton @click="toggleModal">
        Add Membership <PlusIcon class="h-3 w-3 ml-2"
      /></SeraButton>
    </header>

    <div
      class="memberships-segmented"
      role="tablist"
      aria-label="Filter memberships"
      data-cy="membership-filter-toggle"
    >
      <button
        type="button"
        role="tab"
        :aria-selected="statusFilter === 'active'"
        class="memberships-segmented__option"
        :class="{ 'is-active': statusFilter === 'active' }"
        data-cy="membership-filter-active"
        @click="setStatusFilter('active')"
      >
        Active
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="statusFilter === 'all'"
        class="memberships-segmented__option"
        :class="{ 'is-active': statusFilter === 'all' }"
        data-cy="membership-filter-all"
        @click="setStatusFilter('all')"
      >
        All
      </button>
    </div>

    <div class="memberships-list">
      <CustomerMembershipDetails
        v-for="membership in membershipData"
        :key="membership.id"
        :membership="membership"
        :customer="currentUser"
        @edit-membership="editUserMembership(membership)"
      />
      <div v-if="!membershipData.length" class="memberships-empty mb-2">
        <svg
          class="memberships-empty__art"
          viewBox="0 0 80 80"
          fill="none"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="memberships-empty-grid"
              x="0"
              y="0"
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1" cy="1" r="0.6" fill="#cbd5e1" />
            </pattern>
          </defs>
          <rect
            x="2"
            y="2"
            width="76"
            height="76"
            rx="10"
            fill="url(#memberships-empty-grid)"
            opacity="0.5"
          />
          <rect
            x="14"
            y="22"
            width="52"
            height="36"
            rx="6"
            fill="white"
            stroke="#94a3b8"
            stroke-width="1.5"
          />
          <circle cx="26" cy="36" r="5" stroke="#94a3b8" stroke-width="1.5" />
          <rect
            x="36"
            y="32"
            width="22"
            height="2.5"
            rx="1.25"
            fill="#cbd5e1"
          />
          <rect
            x="36"
            y="38"
            width="14"
            height="2.5"
            rx="1.25"
            fill="#cbd5e1"
          />
          <path
            d="M52 50 L54 54 L58.5 54.5 L55.25 57.5 L56 62 L52 59.75 L48 62 L48.75 57.5 L45.5 54.5 L50 54 Z"
            fill="#fbbf24"
            stroke="#f59e0b"
            stroke-width="0.5"
            stroke-linejoin="round"
          />
        </svg>
        <p class="memberships-empty__title">{{ emptyStateTitle }}</p>
        <p class="memberships-empty__hint">{{ emptyStateHint }}</p>
        <div class="memberships-empty__actions">
          <button
            v-if="statusFilter === 'active'"
            type="button"
            class="memberships-empty__link"
            data-cy="memberships-empty-show-all"
            @click="setStatusFilter('all')"
          >
            Show all memberships
          </button>
          <SeraButton
            v-else
            data-cy="memberships-empty-add"
            @click="toggleModal"
          >
            Add Membership <PlusIcon class="h-3 w-3 ml-2" />
          </SeraButton>
        </div>
      </div>
    </div>
    <PaginationControls :pager="membershipPager" @update:data="updateData" />
    <CustomerMembershipModal
      v-if="modal"
      :membership="selectedMembership"
      @hide-modal="toggleModal"
      @action-success="refreshUser"
    />
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import { mapActions, mapState } from "vuex";
import Pagination from "@/lib/pagination";

import PaginationControls from "@/components/PaginationControls.vue";
import SeraButton from "@/components/Seranova/Buttons/Button.vue";
import PlusIcon from "@/images/plus-outline.svg?component";
import CustomerMembershipModal from "@/MembershipModal/CustomerMembershipModal.vue";
import CustomerMembershipDetails from "./CustomerMembershipDetails.vue";

export default {
  components: {
    PaginationControls,
    PlusIcon,
    SeraButton,
    CustomerMembershipDetails,
    CustomerMembershipModal
  },
  data() {
    return {
      modal: false,
      selectedMembership: {},
      membershipData: [],
      membershipPager: {},
      totalMembership: 0,
      statusFilter: "active"
    };
  },
  computed: {
    ...mapState(["currentUser"]),
    membershipPagerLoaded() {
      return !isEmpty(this.membershipPager);
    },
    emptyStateMessage() {
      return this.statusFilter === "active"
        ? "No active memberships"
        : "Not a Member";
    },
    emptyStateTitle() {
      return this.statusFilter === "active"
        ? "No active memberships"
        : "Not a member yet";
    },
    emptyStateHint() {
      return this.statusFilter === "active"
        ? "Expired memberships are hidden. Select All to view the full history."
        : "Enroll this customer to track covered tasks, renewal dates, and service utilization.";
    },
    subtitleText() {
      const count = this.totalMembership;
      if (count === 0) return "No memberships to show";
      const noun = count === 1 ? "membership" : "memberships";
      const scope = this.statusFilter === "active" ? "active" : "total";
      return `${count} ${scope} ${noun}`;
    }
  },
  watch: {
    currentUser() {
      this.init();
    }
  },
  mounted() {
    // Get up to date memberships when opening tab
    if (this.currentUser) {
      this.init();
    }
  },
  methods: {
    ...mapActions(["fetchUser"]),
    toggleModal() {
      this.modal = !this.modal;
      if (!this.modal) {
        this.selectedMembership = {};
      }
    },
    refreshUser() {
      this.fetchUser({ id: this.currentUser.id });
    },
    editUserMembership(membership) {
      this.selectedMembership = membership;
      this.toggleModal();
    },
    async fetchData() {
      const response = await this.membershipPager.getData();
      if (response) {
        this.membershipData = response.data.data;
        this.totalMembership =
          response.data.meta?.pagination.total_objects || 0;
      }
    },
    updateData(val) {
      this.membershipData = val;
    },
    init() {
      this.membershipPager = new Pagination({
        baseURL: this.currentUser.links.fetch_memberships_paginated,
        perPage: 10,
        filteredBy: [{ key: "status", value: this.statusFilter }]
      });
      this.fetchData();
    },
    setStatusFilter(value) {
      if (this.statusFilter === value) return;
      this.statusFilter = value;
      this.membershipPager.filter({ key: "status", value });
      this.membershipPager.page = 1;
      this.fetchData();
    }
  }
};
</script>

<style lang="scss" scoped>
.memberships-tab {
  padding-bottom: 2rem;
}

.memberships-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 0.875rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--gray-border-color);
}
.memberships-subtitle {
  margin: 0.125rem 0 0;
  color: var(--gray);
  font-size: 0.8125rem;
  line-height: 1.2;
}

.memberships-segmented {
  display: inline-flex;
  padding: 3px;
  border-radius: 8px;
  background: #eef0f4;
  margin-bottom: 1rem;
  gap: 2px;
}
.memberships-segmented__option {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.375rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
  border-radius: 6px;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, box-shadow 120ms ease;
  letter-spacing: 0.01em;

  &:hover:not(.is-active) {
    color: #1f2937;
  }
  &.is-active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08),
      0 0 0 1px rgba(15, 23, 42, 0.04);
  }
  &:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 2px;
  }
}

.memberships-list {
  padding: 0;
}

.memberships-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  background: #fbfcfe;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
}
.memberships-empty__art {
  width: 88px;
  height: 88px;
  margin-bottom: 1rem;
}
.memberships-empty__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.005em;
}
.memberships-empty__hint {
  margin: 0.375rem 0 0;
  font-size: 0.8125rem;
  color: var(--gray);
  max-width: 24rem;
  line-height: 1.55;
}
.memberships-empty__actions {
  margin-top: 1.25rem;
}
.memberships-empty__link {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--blue);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;

  &:hover {
    background: #eff6ff;
  }
  &:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 1px;
  }
}
</style>
